/**
 * LeadsCardView — reusable ops card grid, optionally grouped by created period.
 */

import React, { useMemo, useState } from "react";
import { Typography, Button, Switch, Space, Input } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Box, CircularProgress, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import LeadOpsCard from "./LeadOpsCard.jsx";
import { groupLeadsByCreatedPeriod } from "../services/leadCreatedPeriod.js";

const { Text, Title } = Typography;

const Toolbar = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
  marginBottom: 16,
});

const PeriodSection = styled(Box)({
  marginBottom: 24,
});

const PeriodHeading = styled(Box)({
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  marginBottom: 12,
  paddingLeft: 2,
});

const EmptyState = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 16px",
  textAlign: "center",
});

const matchesSearch = (lead, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = [
    lead.name,
    lead.phone,
    lead.referenceNumber,
    lead.moving_from,
    lead.moving_to,
    lead.from,
    lead.to,
    lead.status,
    lead.notes,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

/**
 * @param {object} props
 * @param {object[]} [props.leads]
 * @param {boolean} [props.loading]
 * @param {(lead: object) => void} [props.onViewLead]
 * @param {() => void} [props.onRefresh]
 * @param {(lead: object) => void} [props.onFollowUpNavigate]
 * @param {(lead: object, e: object) => void} [props.onRouteClick]
 * @param {(lead: object) => void} [props.onRowClick]
 * @param {boolean} [props.groupByCreatedPeriod] initial grouping
 * @param {boolean} [props.showSearch]
 * @param {string} [props.title]
 * @param {number} [props.columns] desktop grid columns (default 3)
 */
const LeadsCardView = ({
  leads = [],
  loading = false,
  onViewLead,
  onRefresh,
  onFollowUpNavigate,
  onRouteClick,
  onRowClick,
  groupByCreatedPeriod: groupByCreatedPeriodProp,
  showSearch = true,
  title = "Leads",
  columns = 3,
}) => {
  const [groupByCreated, setGroupByCreated] = useState(
    groupByCreatedPeriodProp ?? true
  );
  const [search, setSearch] = useState("");

  const filteredLeads = useMemo(
    () => leads.filter((lead) => matchesSearch(lead, search.trim())),
    [leads, search]
  );

  const periodGroups = useMemo(
    () =>
      groupByCreated ? groupLeadsByCreatedPeriod(filteredLeads) : null,
    [groupByCreated, filteredLeads]
  );

  const handleCardClick = onRowClick ?? onViewLead;

  const mdSpan = columns >= 3 ? 4 : columns === 2 ? 6 : 12;
  const lgSpan = columns >= 3 ? 4 : columns === 2 ? 6 : 12;

  const toolbarControls = (
    <Space size="middle" wrap>
      {showSearch && (
        <Input
          allowClear
          placeholder="Search leads..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220 }}
          size="small"
        />
      )}
      <Space size="small">
        <Text type="secondary" style={{ fontSize: 12 }}>
          Group by created
        </Text>
        <Switch
          size="small"
          checked={groupByCreated}
          onChange={setGroupByCreated}
        />
      </Space>
      {onRefresh && (
        <Button icon={<ReloadOutlined />} onClick={onRefresh} size="small">
          Refresh
        </Button>
      )}
    </Space>
  );

  const renderCardGrid = (items) => (
    <Grid container spacing={1.5}>
      {items.map((lead) => {
        const key = lead._id || lead.id;
        return (
          <Grid item xs={12} sm={6} md={mdSpan} lg={lgSpan} key={key}>
            <LeadOpsCard
              lead={lead}
              onClick={handleCardClick}
              onRouteClick={onRouteClick}
              onFollowUpNavigate={onFollowUpNavigate}
            />
          </Grid>
        );
      })}
    </Grid>
  );

  if (loading) {
    return (
      <EmptyState>
        <CircularProgress size={32} />
        <Text type="secondary" style={{ marginTop: 12 }}>
          Loading leads...
        </Text>
      </EmptyState>
    );
  }

  return (
    <Box width="100%">
      <Toolbar>
        <Title level={5} style={{ margin: 0 }}>
          {title}
          {filteredLeads.length > 0 && (
            <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
              ({filteredLeads.length})
            </Text>
          )}
        </Title>
        {toolbarControls}
      </Toolbar>

      {filteredLeads.length === 0 ? (
        <EmptyState>
          <Text type="secondary">No leads</Text>
        </EmptyState>
      ) : groupByCreated ? (
        periodGroups.map((group) => (
          <PeriodSection key={group.period}>
            <PeriodHeading>
              <Title level={5} style={{ margin: 0, fontSize: 15 }}>
                {group.label}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {group.leads.length}
              </Text>
            </PeriodHeading>
            {renderCardGrid(group.leads)}
          </PeriodSection>
        ))
      ) : (
        renderCardGrid(filteredLeads)
      )}
    </Box>
  );
};

export default LeadsCardView;
